
import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { LeaderboardEntry } from './LeaderboardTypes';
import { Award, Share } from 'lucide-react';
import { zones } from './LeaderboardTypes';
import ShareModal from '../ShareModal';
import { User } from '@/types/user';

interface LeaderboardTableProps {
  sortedData: LeaderboardEntry[];
  selectedZone: string | null;
  currentUser: User;
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({ 
  sortedData, 
  selectedZone,
  currentUser 
}) => {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(null);
  
  // Get the zone name if a zone is selected
  const activeZoneName = selectedZone ? zones.find(z => z.id === selectedZone)?.name : null;
  
  const handleShareUserRank = (entry: LeaderboardEntry) => {
    setSelectedEntry(entry);
    setShareModalOpen(true);
  };
  
  return (
    <>
      <div className="space-y-2">
        {sortedData.slice(0, 10).map((entry, index) => {
          const isCurrentUser = entry.id === currentUser.id;
          
          return (
            <div 
              key={entry.id}
              className={`flex items-center justify-between p-4 rounded-lg ${
                isCurrentUser ? 'bg-hormony-beige/30' : 'bg-hormony-beige/10'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${
                  index < 3 ? 'bg-hormony-coral text-white' : 'bg-gray-100 text-hormony-charcoal'
                }`}>
                  {index + 1}
                </div>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={entry.avatar} alt={entry.name} />
                  <AvatarFallback>{entry.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium flex items-center">
                    {entry.name}
                    {isCurrentUser && (
                      <span className="ml-2 text-xs bg-hormony-coral/20 text-hormony-coral rounded-full px-2 py-0.5">You</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {entry.streak} day streak
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-semibold text-hormony-coral">
                    {selectedZone ? entry.zones?.[selectedZone] || 0 : entry.points}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedZone ? `${activeZoneName} points` : 'Total points'}
                  </div>
                </div>
                
                {isCurrentUser && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-hormony-coral"
                    onClick={() => handleShareUserRank(entry)}
                  >
                    <Share size={16} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {selectedEntry && (
        <ShareModal 
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
          content={{
            type: 'achievement',
            title: `Rank #${sortedData.findIndex(item => item.id === currentUser.id) + 1} on Hormony Health`,
            description: selectedZone 
              ? `I've reached rank #${sortedData.findIndex(item => item.id === currentUser.id) + 1} in the ${activeZoneName} zone!` 
              : `I've reached rank #${sortedData.findIndex(item => item.id === currentUser.id) + 1} on the leaderboard!`,
            points: selectedZone ? selectedEntry.zones?.[selectedZone] : selectedEntry.points,
            streak: selectedEntry.streak,
            leaderboardEntry: selectedEntry
          }}
        />
      )}
    </>
  );
};

export default LeaderboardTable;
